import { groupBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Button } from '../../components/Button';
import { GetDataBox } from '../../components/GetData';
import { useStoreData } from '../../hooks';
import { media } from '../../services/media';
import { isEmptyObject } from '../../utils';

const mapMessagesGroups = {
    taxReturn: 'Skattemelding (forhåndsutfylt)',
    taxRf: 'Skattemelding (innsendt)',
    taxSettlement: 'Skatteoppgjøret',
    shares: 'Detaljert aksjeoppgave',
    companyTax: 'Skattemelding & Næringsoppgave 2',
    companyAccounting: 'Årsregnskap med noter',
    companyShareholdres: 'Aksjonærregisteroppgaven',
} as any;

export const AltinnSuccess = () => {
    const [companyGroups, setCompanyGroups] = useState<any>(null);
    const { processedMessages } = useStoreData().user;

    useEffect(() => {
        if (
            processedMessages &&
            processedMessages.findIndex((m) => m.entityType === 'COMPANY') !==
                -1
        ) {
            setCompanyGroups(
                groupBy(
                    processedMessages.filter((m) => m.entityType === 'COMPANY'),
                    (message) => message.target,
                ),
            );
        }
    }, []);

    const renderGroups = useCallback(() => {
        const markup = [];

        for (const [key, value] of Object.entries(companyGroups)) {
            markup.push(
                <React.Fragment key={key}>
                    <ResultGroup>Selskapsdokumenter: {key}</ResultGroup>
                    {value &&
                        Array.isArray(value) &&
                        value.map((gm, index) => {
                            return (
                                <Result status={gm.status} key={index}>
                                    {mapMessagesGroups[gm.type]}
                                </Result>
                            );
                        })}
                </React.Fragment>,
            );
        }

        return markup;
    }, [companyGroups]);

    /**
     * Missing `processedMessages` means that user did not completed Altinn
     * flow and tried to access success route.
     *
     * This redirection is tied with Formue's get-data Altinn flow path.
     * If this Route would need to be reusable in other projects we would need
     * to get this path from CMS (from each project version)
     */
    if (!processedMessages || !processedMessages.length) {
        return <Navigate to="/get-data" />;
    }

    return (
        <Section>
            <GetDataBox style={{ backgroundColor: '#437047' }}>
                <Wrapper>
                    <Heading>
                        Dine data er hentet og trygt lagret på din profil
                    </Heading>
                    <SubHeading>Følgende dokumenter er lagret:</SubHeading>
                    {processedMessages &&
                        processedMessages.findIndex(
                            (m) => m.entityType === 'PERSON',
                        ) !== -1 && <ResultGroup>Deg som person:</ResultGroup>}
                    {processedMessages &&
                        processedMessages.map((m, index: number) => {
                            if (m.entityType === 'PERSON') {
                                return (
                                    <Result status={m.status} key={index}>
                                        {mapMessagesGroups[m.type]}
                                    </Result>
                                );
                            }
                        })}

                    {processedMessages &&
                        processedMessages.findIndex(
                            (m) => m.entityType === 'COMPANY',
                        ) !== -1}

                    {!isEmptyObject(companyGroups) && renderGroups()}

                    <Footer>
                        Klikk på knappen for å gå tilbake til din profil
                    </Footer>
                    <Button
                        variant="formue-boxed"
                        className=""
                        onClick={() => {
                            window.location.href =
                                sessionStorage.getItem('redirectUrl') ||
                                'https://formue.no/';
                        }}
                    >
                        Fortsett
                    </Button>
                </Wrapper>
            </GetDataBox>
        </Section>
    );
};

const Section = styled.section`
    display: flex;
    padding: 40px 0;
    color: white;
    min-height: calc(100vh - 80px);
    align-items: center;

    ${media.min('md')} {
        padding: 80px 0;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 16px;
`;

const Heading = styled.h2`
    color: inherit;
    font-size: 30px;
    text-align: center;
`;

const SubHeading = styled.h4`
    color: inherit;
    font-size: 20px;
`;

const ResultGroup = styled.div`
    font-size: 22px;
    text-align: left;
    width: 100%;
    margin: 15px 0;
`;

const ResultSucceed = css`
    &:before {
        content: '';
        width: 4px;
        height: 12px;

        transform: rotate(45deg);
        border-right: 1px solid;
        border-bottom: 1px solid;
        transition: opacity 0.3s;

        width: 8px;
        height: 18px;
        top: 5px;
        left: -20px;

        border-right: 2px solid;
        border-bottom: 2px solid;
    }
`;

const ResultFailed = css`
    &:before {
        content: '✗';
        left: -24px;
        opacity: 0.7;
    }
`;

const Result = styled.div<{ status: 'succeed' | 'failed' }>`
    width: 100%;
    text-align: left;
    font-size: 18px;
    position: relative;
    margin: 0 0 0 40px;

    &:last-of-type {
        margin: 0 0 15px 40px;
    }

    &:before {
        position: absolute;
        top: 0;
        left: 8px;
    }

    ${(props) => props.status === 'succeed' && ResultSucceed}
    ${(props) => props.status === 'failed' && ResultFailed}
`;

const Footer = styled.div`
    padding: 20px 0;
`;
